/**
 * SetPasswordCard — Profile settings section.
 *
 * Detects whether the logged-in user already has a `password_hash` on file:
 *   • YES → renders a short read-only note pointing to the /forgot-password
 *     flow if they want to change it (OTP-guarded).
 *   • NO  → renders a "Set Password" form that POSTs to /api/auth/set-password
 *     and unlocks dual-access (Google + email/password) on the account.
 *
 * This is the missing piece that lets Google-signup users add a password to
 * their account without going through the phone OTP flow first.
 */
import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Lock, KeyRound, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import PasswordField from './PasswordField';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SetPasswordCard() {
  const [status, setStatus] = useState('loading'); // loading | has | missing
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [pastedNotice, setPastedNotice] = useState(false);

  const refresh = async () => {
    try {
      const res = await fetch(`${API}/api/auth/has-password`, { credentials: 'include' });
      const data = await res.json();
      setStatus(data.has_password ? 'has' : 'missing');
    } catch (_err) {
      setStatus('missing'); // safe default — let user try to set one
    }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/set-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to set password');
        return;
      }
      toast.success('Password set — you can now sign in with email + password too.');
      setPassword(''); setConfirm(''); setShow(false);
      setStatus('has');
    } catch (_err) {
      toast.error('Network error');
    } finally { setSaving(false); }
  };

  if (status === 'loading') {
    return (
      <Card className="p-4 bg-zinc-50 border border-zinc-200" data-testid="set-password-loading">
        <p className="text-sm text-zinc-500">Checking your sign-in methods…</p>
      </Card>
    );
  }

  if (status === 'has') {
    return (
      <Card className="p-4 bg-emerald-50 border border-emerald-300" data-testid="set-password-has">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-emerald-900 flex items-center gap-2">
              Email + password sign-in is enabled
              <Badge className="bg-emerald-600 text-white text-[10px]">Dual-access</Badge>
            </div>
            <p className="text-xs text-emerald-800 mt-1">
              You can sign in with either Google or your email + password. To rotate your
              password, use <a href="/forgot-password" className="underline font-semibold">Forgot Password</a> —
              we&apos;ll text a 6-digit code to your phone.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // status === 'missing' — Google-signup account with no password yet.
  return (
    <Card className="p-4 bg-amber-50 border border-amber-300" data-testid="set-password-missing">
      <div className="flex items-start gap-3">
        <KeyRound className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-amber-900 flex items-center gap-2">
            Add a password
            <Badge className="bg-amber-600 text-white text-[10px]">Recommended</Badge>
          </div>
          <p className="text-xs text-amber-800 mt-1">
            Your account currently uses Google sign-in only. Add a password so you can log in
            directly without needing Google — perfect backup for admin accounts.
          </p>

          <form onSubmit={submit} className="mt-3 space-y-2" data-testid="set-password-form">
            <PasswordField
              label="New password"
              required
              minLength={8}
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              showStrength
              onPasteWarning={setPastedNotice}
              reveal={show}
              onToggleReveal={() => setShow((s) => !s)}
              testId="set-password-new"
            />
            <PasswordField
              label="Confirm password"
              required
              minLength={8}
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              matchValue={password}
              onPasteWarning={setPastedNotice}
              reveal={show}
              onToggleReveal={() => setShow((s) => !s)}
              testId="set-password-confirm"
            />
            {pastedNotice && (
              <p
                className="text-[10px] font-bold px-2 py-1.5 rounded"
                style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#b45309', border: '1px solid rgba(217,119,6,0.4)' }}
                data-testid="set-password-paste-guard"
              >
                ⚠️ You pasted content that looks like an email or a company name. Passwords should never contain your identity.
              </p>
            )}
            <Button
              type="submit"
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
              data-testid="set-password-submit"
            >
              <Lock className="h-4 w-4 mr-2" />
              {saving ? 'Setting password…' : 'Set password'}
            </Button>
          </form>
        </div>
      </div>
    </Card>
  );
}
